// Column heightmaps (ENG-6), maintained incrementally on edit. WORLD_SURFACE is
// the Y of the lowest air cell above the topmost non-air block (i.e. surfaceY =
// highest non-air Y + 1). MOTION_BLOCKING tracks the highest motion-blocking
// block; for the M0.3 block set (all full cubes) the two coincide, but they are
// kept as distinct arrays because later blocks (plants, fluids) diverge them.
import { WORLD } from '../config';

export const COLUMNS = WORLD.SECTION * WORLD.SECTION; // 256

/** Sentinel below the world floor meaning "column is entirely air". Per TAL-5 the
 *  sentinel is the dimension's own `minY` (strictly below any real surfaceY =
 *  highest-non-air + 1). This constant is the default-range value; tall
 *  dimensions fill/compare against their `range.minY` (still the same predicate). */
export const NO_SURFACE = WORLD.MIN_Y;

export function columnIndex(lx: number, lz: number): number {
  return ((lz & 15) << 4) | (lx & 15);
}

export interface Heightmaps {
  worldSurface: Int16Array; // 256; surfaceY = highest non-air + 1, or NO_SURFACE
  motionBlocking: Int16Array;
}

export function createHeightmaps(noSurface: number = NO_SURFACE): Heightmaps {
  const worldSurface = new Int16Array(COLUMNS).fill(noSurface);
  const motionBlocking = new Int16Array(COLUMNS).fill(noSurface);
  return { worldSurface, motionBlocking };
}

/**
 * Update one column's heightmap after a block change at world-Y `y`.
 * `nowBlocking` = the new occupancy; `scanDown` finds the next lower blocking Y
 * when the top block was removed. O(1) except when clearing the current top.
 */
export function updateHeightOnSet(
  height: Int16Array,
  col: number,
  y: number,
  nowBlocking: boolean,
  scanDown: (fromY: number) => number,
): void {
  const current = height[col]!;
  if (nowBlocking) {
    if (y + 1 > current) height[col] = y + 1;
  } else if (y + 1 === current) {
    // Removed the top block — walk down for the next blocking cell.
    height[col] = scanDown(y - 1);
  }
}
